<script setup lang="ts">
import { computed, onMounted } from "vue"
import { useRoute, useRouter } from "vue-router/composables"
import { isNullish } from "@tinyurldev/tu-ui-vue"

import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue"
import OrgDomainsSettings from "@Modules/teams/Views/OrgDomainsSettings.vue"
import { XTeamsStore } from "@Modules/teams/Store/XTeamsStore.ts"
import { XDomainsStore } from "@Modules/urls/Store/XDomainsStore"
import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts"
import DomainsApi from "@Modules/teams/Api/DomainsApi"
import XOverlay from "@Modules/shared/store/XOverlay"
import BYODomainPopover from "@Modules/shared/Components/BYODomainPopover.vue"
import BYOSubDomainPopover from "@Modules/shared/Components/BYOSubDomainPopover.vue"
import type { Domain } from "@Modules/urls/Data/Models/DomainModel"
import EditDomainPopover from "@Modules/shared/Components/EditDomainPopover.vue"
import DeleteDomainPopover from "@Modules/shared/Components/DeleteDomainPopover.vue"

//
// Setup
const route = useRoute()
const router = useRouter()
const teams = XTeamsStore()
const domainsStore = XDomainsStore()
const domainsApi = DomainsApi()
const overlay = XOverlay();

//
// Computed

/** The slug of the current organization and team */
const orgSlug = computed<string>(() => route?.params?.org || "")
const teamSlug = computed<string | null>(() => route.params?.team || null)

/** The currently loaded organization */
const currentOrganization = computed<Organization | null>(() =>
    teams.getOrganizationBySlug(orgSlug.value),
)

/** The currently chosen workspace. Personal team is 'personal'. Null if no workspace chosen */
const currentWorkspace = computed(() => {
    if (isNullish(teamSlug.value)) {
        return null
    }

    return teams.getWorkspaceBySlugAndTeam(orgSlug.value, teamSlug.value) ?? null
})

/** DNS nameservers for domain setup */
const nameservers = computed(() => domainsStore.nameservers)

/** Personal domain (CNAME target) for subdomain setup */
const personalDomain = computed(() => currentWorkspace.value?.domains?.personal)

//
// Methods

function openBYODomainPopover(): void {
  overlay.show(
    BYODomainPopover,
    {
      errors: domainsApi.errors.value,
      loading: domainsApi.loading.value,
      nameservers: nameservers.value,
      createDomain: (domain: string) => domainsApi.createBrandedDomain(domain, orgSlug.value, true),
      checkDomain: (domain: string) => domainsApi.checkBrandedDomain(domain, orgSlug.value),
    },
    {
      "domain-created": () => refreshDomains(),
    },
  );
}

function openBYOSubDomainPopover(): void {
  overlay.show(
    BYOSubDomainPopover,
    {
      errors: domainsApi.errors.value,
      loading: domainsApi.loading.value,
      personalDomain: personalDomain.value,
      createSubDomain: (domain: string) => domainsApi.createBrandedDomain(domain, orgSlug.value, false),
      checkSubDomain: (domain: string) => domainsApi.checkBrandedDomain(domain, orgSlug.value),
    },
    {
      "domain-created": () => refreshDomains(),
    },
  );
}

function openDeleteDomainPopover(domain: Domain): void {
  overlay.show(
    DeleteDomainPopover,
    {
      domain,
      deleteDomain: (domain: string) => domainsApi.deleteDomain(domain, orgSlug.value),
      restoreDomain: (domain: string) => domainsApi.restoreDomain(domain, orgSlug.value),
    },
    {
      delete: () => refreshDomains(),
      restore: () => refreshDomains(),
    },
  );
}

function openEditDomainPopover(domain: Domain): void {
  if (domain.isDeleted) {
    openDeleteDomainPopover(domain);

    return
  }

  overlay.show(
    EditDomainPopover,
    {
      domain,
      nameservers: nameservers.value,
      personalDomain: personalDomain.value,
      isRenewalEnabled: domain.isRenewalEnabled ?? false,
      checkDomain: (domain: string) => domainsApi.checkBrandedDomain(domain, orgSlug.value),
      checkSubDomain: (domain: string) => domainsApi.checkBrandedDomain(domain, orgSlug.value),
      toggleDisableDomain: (domain: string) => domainsApi.toggleDisableDomain(domain, orgSlug.value),
      setDomainRootRedirect: (domain: string, enabled: boolean, destination: string) => domainsApi.setDomainRootRedirect(domain, enabled, destination, orgSlug.value),
      setDomainErrorRedirect: (domain: string, enabled: boolean, destination: string) => domainsApi.setDomainErrorRedirect(domain, enabled, destination, orgSlug.value),
      setDomainRenewal: (domain: string, enabled: boolean) => domainsApi.setDomainRenewal(domain, enabled, orgSlug.value),
    },
    {
      delete: (domain: Domain) => openDeleteDomainPopover(domain),
      update: () => refreshDomains(),
    },
  );
}

function navigateToPurchaseDomain(): void {
  if (!currentOrganization.value) {
    return
  }

  router.push({
    name: "org.purchase.domain",
    params: {
      org: orgSlug.value,
    },
  })
}

const refreshDomains = () => {
  if (!currentOrganization.value || !currentOrganization.value.can("manage_org_domains")) {
    return;
  }

  domainsApi.fetchOrgDomains({ orgSlug: currentOrganization.value.slug });
}

onMounted(() => {
  refreshDomains()
})
</script>

<template>
  <OrgSettingsLayout
    v-if="currentOrganization"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
  >
    <OrgDomainsSettings
      :organization="currentOrganization"
      :domains="domainsApi.domains.value"
      :loading="domainsApi.loading.value"
      @add-domain="openBYODomainPopover"
      @add-subdomain="openBYOSubDomainPopover"
      @domain-action="openEditDomainPopover"
      @purchase-domain="navigateToPurchaseDomain"
    />
  </OrgSettingsLayout>
</template>
